// ── AspreoShell ──────────────────────────────────────────────────────────
// The Tactile Archive shell: sidebar + topbar. Visual replacement for the
// old Navbar.js — same view ids, same data hooks, same routing, only the
// presentation layer changed. See src/styles/tactile-archive.css for tokens.
import { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { signOut, supabase } from '../lib/supabase';
import { Avatar } from './UI';
import { useThemeToggle, useIsDark } from '../DiaryApp';

const MIcon = ({ name, filled = false, size = 18, style = {} }) => (
  <span
    className="material-symbols-outlined"
    style={{
      fontSize: size,
      fontVariationSettings: filled ? "'FILL' 1,'wght' 500,'GRAD' 0,'opsz' 24" : "'FILL' 0,'wght' 400,'GRAD' 0,'opsz' 24",
      userSelect: 'none',
      lineHeight: 1,
      flexShrink: 0,
      ...style,
    }}
  >
    {name}
  </span>
);

// Grouped per the Tactile Archive's Main/Execution/Personal/Knowledge/System
// taxonomy. View ids are unchanged from the old Navbar — routing in
// App.js/DiaryApp.js is untouched, only the nav chrome around it changed.
const NAV_SECTIONS = [
  {
    label: null,
    items: [{ id: 'dashboard', icon: 'dashboard', label: 'Dashboard' }],
  },
  {
    label: 'Execution',
    items: [
      { id: 'weekly',   icon: 'calendar_view_week', label: 'Weekly'    },
      { id: 'monthly',  icon: 'calendar_month',     label: 'Calendar'  },
      { id: 'projects', icon: 'folder_open',        label: 'Projects'  },
      { id: 'deepwork', icon: 'timer',              label: 'Deep Work' },
    ],
  },
  {
    label: 'Personal',
    items: [
      { id: 'goals',       icon: 'target',                   label: 'Goals'         },
      { id: 'visions',     icon: 'auto_awesome',              label: 'Visions'       },
      { id: 'habits',      icon: 'local_fire_department',     label: 'Habits'        },
      { id: 'fitness',     icon: 'nutrition',                 label: 'Fitness & Diet'},
      { id: 'reflections', icon: 'self_improvement',          label: 'Journal'       },
    ],
  },
  {
    label: 'Knowledge',
    items: [
      { id: 'wiki',    icon: 'menu_book',   label: 'Knowledge' },
      { id: 'books',   icon: 'book',        label: 'Books'     },
      { id: 'ideas',   icon: 'lightbulb',   label: 'Ideas'     },
      { id: 'content', icon: 'edit_note',   label: 'Content'   },
    ],
  },
  {
    label: 'System',
    items: [
      { id: 'finance',       icon: 'payments',          label: 'Finance'      },
      { id: 'vault',         icon: 'inventory_2',       label: 'Vault'        },
      { id: 'analytics',     icon: 'monitoring',        label: 'Analytics'    },
      { id: 'business',      icon: 'business_center',   label: 'Business'     },
      { id: 'meetings',      icon: 'event',             label: 'Meetings'     },
      { id: 'payments',      icon: 'payments',          label: 'Payments'     },
      { id: 'integrations',  icon: 'hub',               label: 'Integrations' },
    ],
  },
];

const ALL_ITEMS = NAV_SECTIONS.flatMap(g => g.items);
const NAV_FLAG_MAP = {
  wiki: 'wiki', integrations: 'integrations', business: 'business',
  meetings: 'meetings', payments: 'payments', content: 'content',
  projects: 'projects', vault: 'vault',
};

export default function AspreoShell({ view, setView, onBackup, onShowNewsletter, featureFlags = {}, children }) {
  const { profile, unreadCount, notifications, markNotifRead, markAllRead } = useAuth();

  // Single source of truth: DiaryApp's ThemeProvider. Previously this shell kept
  // its own isDark state (localStorage + a custom event), which only ever moved
  // the `data-theme` attribute — every useC()-sourced color across the app's
  // older screens comes from ThemeProvider's own React state, which that local
  // toggle never updated, so those screens silently stayed light. Consuming the
  // context directly makes the toggle actually reach them.
  const isDark = useIsDark();
  const toggleTheme = useThemeToggle();

  const [showNotifs, setShowNotifs] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showDelModal, setShowDelModal] = useState(false);
  const [delConfirm, setDelConfirm] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [delError, setDelError] = useState('');
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  useEffect(() => {
    const handler = () => { setShowNotifs(false); setShowProfile(false); };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, []);

  const handleSignOut = () => signOut();

  const handleDeleteAccount = async () => {
    if (delConfirm.trim().toLowerCase() !== 'delete my account') {
      setDelError('Please type exactly: delete my account');
      return;
    }
    setDeleting(true);
    setDelError('');
    try {
      await supabase.rpc('delete_user_data');
      const { error } = await supabase.rpc('delete_auth_user');
      if (error) throw error;
      await signOut();
    } catch (err) {
      setDelError(err.message || 'Failed to delete. Please contact support.');
      setDeleting(false);
    }
  };

  const currentPage = ALL_ITEMS.find(n => n.id === view);
  const initials = (profile?.full_name || profile?.email || 'U')
    .split(' ').map(s => s[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();

  const SidebarNav = () => (
    <>
      <div className="ta-sb-brand">
        <div className="ta-sb-brand-mark">Aspreo<em style={{ fontStyle: 'italic', color: 'var(--ta-primary)' }}>.</em></div>
        <div className="ta-sb-brand-sub">Life OS</div>
      </div>

      {NAV_SECTIONS.map((sec, si) => {
        const items = sec.items.filter(item => {
          const flagKey = NAV_FLAG_MAP[item.id];
          if (flagKey && featureFlags[flagKey] === false) return false;
          return true;
        });
        if (items.length === 0) return null;
        return (
          <div key={si} className="ta-sb-section">
            {sec.label && <div className="ta-sb-section-label">{sec.label}</div>}
            {items.map(item => (
              <button
                key={item.id}
                className={'ta-sb-item' + (view === item.id ? ' active' : '')}
                onClick={() => { setView(item.id); setMobileSidebarOpen(false); }}
              >
                <span className="ta-sb-item-left">
                  <MIcon name={item.icon} filled={view === item.id} size={16} style={{ color: view === item.id ? 'var(--ta-primary)' : 'var(--ta-on-surface-muted)' }} />
                  <span>{item.label}</span>
                </span>
              </button>
            ))}
          </div>
        );
      })}

      <div className="ta-sb-footer">
        <div className="ta-sb-avatar">{initials}</div>
        <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <span className="ta-sb-user-name" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{profile?.full_name || 'User'}</span>
          <span className="ta-sb-user-role">{profile?.role || 'user'}</span>
        </div>
      </div>
    </>
  );

  return (
    <div className="ta-app-frame">
      {/* Desktop sidebar */}
      <aside className="ta-sidebar hide-mobile">
        <SidebarNav />
      </aside>

      {/* Mobile sidebar overlay */}
      {mobileSidebarOpen && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 999, background: 'rgba(0,0,0,.5)', backdropFilter: 'blur(4px)' }} onClick={() => setMobileSidebarOpen(false)}>
          <div className="ta-sidebar" style={{ height: '100%', position: 'static' }} onClick={e => e.stopPropagation()}>
            <SidebarNav />
          </div>
        </div>
      )}

      <div className="ta-main-area" style={{ flex: 1, minWidth: 0 }}>
        <div className="ta-topbar">
          <div className="ta-row ta-gap-3">
            <button
              className="hide-desktop ta-btn-icon"
              onClick={() => setMobileSidebarOpen(true)}
              style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--ta-on-surface-variant)', display: 'flex' }}
            >
              <MIcon name="menu" size={20} />
            </button>
            <div className="ta-topbar-crumbs">
              <span className="here">{currentPage?.label || 'Aspreo'}</span>
            </div>
          </div>

          <div className="ta-topbar-actions">
            <button className="ta-btn ta-btn-ghost hide-mobile" onClick={onShowNewsletter}>
              <MIcon name="forward_to_inbox" size={14} /> Newsletter
            </button>
            <button className="ta-btn-icon" style={{ border: 'none', cursor: 'pointer' }} onClick={toggleTheme} title={isDark ? 'Light mode' : 'Dark mode'}>
              <MIcon name={isDark ? 'light_mode' : 'dark_mode'} size={16} />
            </button>

            <div style={{ position: 'relative' }} onClick={e => e.stopPropagation()}>
              <button className="ta-btn-icon" style={{ border: 'none', cursor: 'pointer', position: 'relative' }} onClick={() => { setShowNotifs(p => !p); setShowProfile(false); }}>
                <MIcon name="notifications" size={16} />
                {unreadCount > 0 && (
                  <span style={{ position: 'absolute', top: -3, right: -3, minWidth: 16, height: 16, borderRadius: 99, background: 'var(--ta-negative)', color: '#fff', fontSize: 9, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 3px', border: '2px solid var(--ta-background)' }}>
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>
              {showNotifs && (
                <div className="ta-card" style={{ position: 'absolute', top: 'calc(100% + 8px)', right: 0, width: 340, padding: 0, zIndex: 200, overflow: 'hidden', boxShadow: '0 20px 45px -20px rgba(26,28,28,.25)' }}>
                  <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--ta-outline-variant)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span className="ta-serif" style={{ fontSize: 15 }}>Notifications</span>
                    {unreadCount > 0 && <button onClick={markAllRead} className="ta-mono" style={{ fontSize: 10, color: 'var(--ta-primary)', background: 'none', border: 'none', cursor: 'pointer', letterSpacing: '.08em', textTransform: 'uppercase' }}>Mark all read</button>}
                  </div>
                  <div style={{ maxHeight: 340, overflowY: 'auto' }}>
                    {notifications.length === 0 ? (
                      <div style={{ padding: '32px 16px', textAlign: 'center' }}>
                        <div className="ta-italic ta-serif ta-muted" style={{ fontSize: 14 }}>Nothing yet.</div>
                      </div>
                    ) : notifications.slice(0, 15).map(n => (
                      <div key={n.id} style={{ padding: '12px 16px', borderBottom: '1px solid var(--ta-outline-variant)', background: n.read ? 'transparent' : 'var(--ta-primary-fixed)', cursor: 'pointer' }}
                        onClick={() => { markNotifRead(n.id); if (n.type === 'fellow_request') setView('feed'); setShowNotifs(false); }}>
                        <div style={{ fontSize: 13, color: 'var(--ta-on-surface)', fontWeight: n.read ? 400 : 600, marginBottom: 2 }}>{n.title || 'Notification'}</div>
                        {n.message && <div style={{ fontSize: 12, color: 'var(--ta-on-surface-muted)', lineHeight: 1.5 }}>{n.message}</div>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div style={{ position: 'relative' }} onClick={e => e.stopPropagation()}>
              <button
                onClick={() => { setShowProfile(p => !p); setShowNotifs(false); }}
                style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 10px 4px 4px', borderRadius: 'var(--r-md)', border: '1px solid var(--ta-outline-variant)', background: 'var(--ta-surface-lowest)', cursor: 'pointer' }}
              >
                <Avatar src={profile?.avatar_url} name={profile?.full_name || profile?.email} size={26} />
                <span className="hide-mobile" style={{ fontSize: 12, fontWeight: 600, color: 'var(--ta-on-surface)' }}>{(profile?.full_name || 'User').split(' ')[0]}</span>
                <MIcon name="keyboard_arrow_down" size={14} style={{ color: 'var(--ta-on-surface-muted)' }} />
              </button>
              {showProfile && (
                <div className="ta-card" style={{ position: 'absolute', top: 'calc(100% + 8px)', right: 0, width: 230, padding: 0, zIndex: 200, overflow: 'hidden', boxShadow: '0 20px 45px -20px rgba(26,28,28,.25)' }}>
                  <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--ta-outline-variant)' }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--ta-on-surface)' }}>{profile?.full_name || 'User'}</div>
                    <div style={{ fontSize: 11, color: 'var(--ta-on-surface-muted)', marginTop: 1 }}>{profile?.email}</div>
                  </div>
                  <div style={{ padding: 6 }}>
                    <button onClick={() => { setView('profile'); setShowProfile(false); }} style={profileMenuItemSx}><MIcon name="person" size={15} style={{ color: 'var(--ta-on-surface-muted)' }} /> My Profile</button>
                    <button onClick={() => { setView('settings'); setShowProfile(false); }} style={profileMenuItemSx}><MIcon name="settings" size={15} style={{ color: 'var(--ta-on-surface-muted)' }} /> Settings</button>
                    <button onClick={onBackup} style={profileMenuItemSx}><MIcon name="download" size={15} style={{ color: 'var(--ta-on-surface-muted)' }} /> Export Backup</button>
                    <div style={{ height: 1, background: 'var(--ta-outline-variant)', margin: '4px 0' }} />
                    <button onClick={handleSignOut} style={{ ...profileMenuItemSx, color: 'var(--ta-negative)' }}><MIcon name="logout" size={15} style={{ color: 'var(--ta-negative)' }} /> Sign Out</button>
                    <div style={{ height: 1, background: 'var(--ta-outline-variant)', margin: '4px 0' }} />
                    <button onClick={() => { setShowProfile(false); setShowDelModal(true); setDelConfirm(''); setDelError(''); }} style={{ ...profileMenuItemSx, color: 'var(--ta-negative)', fontSize: 12 }}>
                      <MIcon name="delete_forever" size={14} style={{ color: 'var(--ta-negative)' }} /> Delete Account
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="ta-content">{children}</div>
      </div>

      {/* Delete account modal */}
      {showDelModal && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,.6)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          onClick={e => e.target === e.currentTarget && !deleting && setShowDelModal(false)}>
          <div className="ta-card-prominent" style={{ width: '100%', maxWidth: 420 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              <div style={{ width: 40, height: 40, borderRadius: 10, background: '#f1dada', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <MIcon name="delete_forever" size={20} style={{ color: 'var(--ta-negative)' }} />
              </div>
              <div>
                <div className="ta-serif" style={{ fontSize: 18 }}>Delete Account</div>
                <div style={{ fontSize: 11, color: 'var(--ta-on-surface-muted)', marginTop: 1 }}>This action is permanent and cannot be undone</div>
              </div>
            </div>
            <div style={{ background: '#f1dada', borderRadius: 10, padding: '12px 14px', marginBottom: 20 }}>
              <div style={{ fontSize: 12, color: 'var(--ta-negative)', lineHeight: 1.7 }}>
                This will permanently remove all your data including goals, habits, entries, finances, visions, books, ideas, and your profile.
              </div>
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--ta-on-surface-muted)', marginBottom: 7, textTransform: 'uppercase', letterSpacing: '.05em' }}>
                Type <strong style={{ color: 'var(--ta-negative)' }}>delete my account</strong> to confirm
              </label>
              <input
                value={delConfirm}
                onChange={e => { setDelConfirm(e.target.value); setDelError(''); }}
                placeholder="delete my account"
                disabled={deleting}
                className="ta-input"
                style={{ borderColor: delError ? 'var(--ta-negative)' : undefined }}
              />
              {delError && <div style={{ fontSize: 11, color: 'var(--ta-negative)', marginTop: 5 }}>{delError}</div>}
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="ta-btn ta-btn-ghost" style={{ flex: 1, justifyContent: 'center' }} onClick={() => { setShowDelModal(false); setDelConfirm(''); setDelError(''); }} disabled={deleting}>Cancel</button>
              <button
                className="ta-btn"
                style={{ flex: 1, justifyContent: 'center', background: (deleting || delConfirm.trim().toLowerCase() !== 'delete my account') ? 'var(--ta-surface-high)' : 'var(--ta-negative)', color: (deleting || delConfirm.trim().toLowerCase() !== 'delete my account') ? 'var(--ta-on-surface-muted)' : '#fff' }}
                disabled={deleting || delConfirm.trim().toLowerCase() !== 'delete my account'}
                onClick={handleDeleteAccount}
              >
                {deleting ? 'Deleting…' : 'Delete Forever'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const profileMenuItemSx = {
  display: 'flex', alignItems: 'center', gap: 8,
  width: '100%', padding: '8px 10px',
  borderRadius: 'var(--r-md)', border: 'none',
  background: 'transparent', color: 'var(--ta-on-surface-variant)',
  fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 500,
  cursor: 'pointer', textAlign: 'left',
};
